import type { PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { effectiveYExtent, toCanvasX, toCanvasY } from "./scale-utils";

export type ScatterRendererStyle = Readonly<{
  fillStyle: string;
  radius: number;
}>;

/** Canvas scatter renderer for scalar and point payloads. */
export class ScatterRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: ScatterRendererStyle = {
    fillStyle: "#2563eb",
    radius: 3,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isScalarEntry(entry) || PlotCollection.isPointEntry(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    const extent = effectiveYExtent(entry, request);
    if (extent === null || extent === undefined) return;

    if (PlotCollection.isScalarEntry(entry)) {
      for (const [x, y] of entry.series.range(
        request.viewRange.start,
        request.viewRange.end,
      ).points()) {
        target.ctx.fillStyle = this.style.fillStyle;
        target.ctx.beginPath();
        target.ctx.arc(
          toCanvasX(x, request.viewRange, target.size.width),
          toCanvasY(y, extent, target.size.height),
          this.style.radius,
          0,
          Math.PI * 2,
        );
        target.ctx.fill();
      }
      return;
    }

    if (!PlotCollection.isPointEntry(entry)) return;

    for (const [x, value] of entry.series.range(
      request.viewRange.start,
      request.viewRange.end,
    ).points()) {
      target.ctx.fillStyle = value.color ?? this.style.fillStyle;
      target.ctx.beginPath();
      target.ctx.arc(
        toCanvasX(x, request.viewRange, target.size.width),
        toCanvasY(value.y, extent, target.size.height),
        value.radius ?? this.style.radius,
        0,
        Math.PI * 2,
      );
      target.ctx.fill();
    }
  }
}
